import { useEffect, useRef, useState } from "react";

import { DATA_BASE_URL, FRESH_S, ID_BASE_URL, REFRESH_MS, STALE_S } from "../config";
import type {
  SnowState,
  Conditions,
  LiveCollection,
  LiveData,
  PlacePage,
  TileLayer,
  TilesManifest,
  IdRecord,
  ReachFeature,
  FlowNetwork,
  Normals,
  SpeciesIndex,
  SpeciesRecord,
} from "./types";

/**
 * An HTTP status the tree answered with.
 *
 * Carried on the error rather than only in its message so a caller can tell
 * "this artifact does not exist" (a 404 is an ordinary answer for the files
 * that exist only for some places) from "the fetch failed" — which must never
 * be reported to a reader as an absence.
 */
export class HttpError extends Error {
  readonly status: number;

  constructor(status: number, statusText: string, url: string) {
    super(`${status} ${statusText} — ${url}`);
    this.name = "HttpError";
    this.status = status;
  }
}

/** Fetch one JSON artifact from the tree. Relative to `DATA_BASE_URL`. */
export async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const url = `${DATA_BASE_URL}/${path.replace(/^\/+/, "")}`;
  const res = await fetch(url, init);
  if (!res.ok) {
    throw new HttpError(res.status, res.statusText, url);
  }
  return (await res.json()) as T;
}

// --- manifest indirection --------------------------------------------------

/**
 * Rewrite `url` so that it points at `toBase` instead of `fromBase`.
 *
 * The tiles manifest is written by the publisher with absolute URLs under
 * `PUBLIC_BASE_URL` — the address the tree *will* have in production. In
 * development that host does not resolve, and a copy of the tree served from
 * anywhere else (a preview deploy, a laptop, a mirror) has a different origin
 * again. Rather than teach the publisher about every consumer, the client
 * rewrites: any URL whose origin (and path prefix) is the manifest's own base
 * is re-pointed at wherever we are actually reading the tree from.
 *
 * Anything else — the AWS terrarium tiles, say — is left exactly as written,
 * because it is genuinely somewhere else. When the real domain exists and
 * `DATA_BASE_URL` equals `PUBLIC_BASE_URL`, this is a no-op.
 */
export function rebaseUrl(
  url: string,
  fromBase: string | null | undefined,
  toBase: string,
): string {
  if (!url || !fromBase) return url;

  let from: URL;
  let target: URL;
  try {
    from = new URL(fromBase);
    target = new URL(url);
  } catch {
    return url; // relative, templated or otherwise not absolute — leave it be
  }
  if (target.origin !== from.origin) return url;

  // Honour a path prefix on the base ("https://host/twin"): only rewrite URLs
  // that actually sit under it, and strip it before re-hanging the remainder.
  const prefix = from.pathname.replace(/\/+$/, "");
  if (prefix && !target.pathname.startsWith(`${prefix}/`)) return url;
  const rest = prefix ? target.pathname.slice(prefix.length) : target.pathname;

  return `${toBase.replace(/\/+$/, "")}${rest}${target.search}${target.hash}`;
}

/**
 * The base the manifest's own URLs were written against.
 *
 * The manifest carries no `base_url` field, so we derive it: the origin of the
 * first archive URL we find, in the order basemap → terrain → imagery. Every
 * archive the publisher writes is hung off the same `PUBLIC_BASE_URL`, so any
 * one of them identifies it. `xyz` layers are ignored on purpose — those are
 * third-party endpoints (AWS terrarium tiles today) that were never ours to
 * rebase. Returns null when the manifest has no archives at all, which makes
 * every rebase a no-op.
 */
export function manifestBaseUrl(manifest: TilesManifest): string | null {
  const layers = manifest?.layers;
  if (!layers) return null;
  for (const layer of [layers.basemap, layers.terrain, layers.imagery]) {
    if (layer?.type !== "pmtiles") continue;
    const url = layer.archives?.[0]?.url;
    if (!url) continue;
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Rebase a **templated** URL by literal prefix substitution.
 *
 * `glyphs` and `sprite` carry MapLibre's own `{fontstack}` and `{range}`
 * tokens, and `new URL()` percent-encodes braces the moment it parses one —
 * `%7Bfontstack%7D` is a path MapLibre will never ask for and our tree will
 * never answer. So this one does string surgery on the origin and touches
 * nothing else. A URL that is not under `fromBase` is somebody else's and is
 * returned unchanged, exactly as in `rebaseUrl`.
 */
export function rebaseTemplate(
  url: string | undefined,
  fromBase: string | null | undefined,
  toBase: string,
): string | undefined {
  if (!url || !fromBase) return url;
  const from = fromBase.replace(/\/+$/, "");
  if (!url.startsWith(`${from}/`)) return url;
  return `${toBase.replace(/\/+$/, "")}${url.slice(from.length)}`;
}

/** A copy of the manifest with every archive URL pointed at `toBase`. */
export function rebaseManifest(
  manifest: TilesManifest,
  toBase: string = DATA_BASE_URL,
): TilesManifest {
  const from = manifestBaseUrl(manifest);
  if (!from) return manifest;

  const rebaseLayer = <T extends TileLayer | null>(layer: T): T => {
    if (!layer || layer.type !== "pmtiles") return layer;
    const glyphs = rebaseTemplate(layer.glyphs, from, toBase);
    const sprite = rebaseTemplate(layer.sprite, from, toBase);
    return {
      ...layer,
      archives: layer.archives.map((a) => ({
        ...a,
        url: rebaseUrl(a.url, from, toBase),
      })),
      ...(glyphs ? { glyphs } : {}),
      ...(sprite ? { sprite } : {}),
    } as T;
  };

  return {
    ...manifest,
    layers: {
      basemap: rebaseLayer(manifest.layers.basemap),
      terrain: rebaseLayer(manifest.layers.terrain),
      imagery: rebaseLayer(manifest.layers.imagery),
    },
  };
}

// --- staleness -------------------------------------------------------------

export type StalenessLevel = "fresh" | "aging" | "stale";

/**
 * How old is this tree, and does that matter?
 *
 * Fresh under ten minutes (the publisher runs every five), aging under an
 * hour, stale past that. An unparseable or missing timestamp is stale: the
 * honest answer to "when was this?" when we cannot tell is "we do not know,
 * assume old", never "just now".
 */
export function stalenessOf(
  generatedAt: string,
  now: number = Date.now(),
): { seconds: number; level: StalenessLevel } {
  const t = Date.parse(generatedAt);
  if (Number.isNaN(t)) {
    return { seconds: Number.POSITIVE_INFINITY, level: "stale" };
  }
  const seconds = Math.max(0, (now - t) / 1000);
  const level: StalenessLevel =
    seconds < FRESH_S ? "fresh" : seconds < STALE_S ? "aging" : "stale";
  return { seconds, level };
}

// --- hooks -----------------------------------------------------------------

export interface ConditionsState {
  data: Conditions | null;
  error: Error | null;
  /** When *we* last successfully read the file — distinct from the tree's
   * own `generated_at`, and the pair is what tells a stuck publisher apart
   * from a broken network. */
  fetchedAt: Date | null;
  /** When the current run of failed refreshes began; null while healthy. */
  failingSince: Date | null;
}

/** One retry, three seconds after a failed poll, before the badge is told. */
export const RETRY_MS = 3_000;

/**
 * Should a tab that just woke up refetch now rather than wait for its timer?
 *
 * Phones suspend timers in background tabs and drop the first fetch on
 * resume, which is how the badge sat on an old timestamp "until I refreshed".
 * Refetch on wake unless the last fetch is recent enough that the timer
 * will beat us to it anyway.
 */
export function shouldRefetchOnWake(
  lastFetchedAt: Date | null,
  now: number,
  refreshMs: number,
): boolean {
  if (!lastFetchedAt) return true;
  return now - lastFetchedAt.getTime() > refreshMs / 2;
}

/** `latest/conditions.json`, re-read on an interval. */
export function useConditions(refreshMs: number = REFRESH_MS): ConditionsState {
  const [state, setState] = useState<ConditionsState>({
    data: null,
    error: null,
    fetchedAt: null,
    failingSince: null,
  });
  const alive = useRef(true);
  /** Mirror of `state.fetchedAt` for the wake listeners, without re-subscribing. */
  const fetchedAtRef = useRef<Date | null>(null);

  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    let retry: number | null = null;
    let inFlight = false;

    const load = async (retrying = false) => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await fetchJson<Conditions>("latest/conditions.json", {
          signal: controller.signal,
          // `latest/` is published with `max-age=60`, and this poll is on a
          // 60-second interval: without this the browser can answer a poll
          // from its own cache and the badge's clock would freeze at a
          // `generated_at` the tree has already moved past. `no-cache` still
          // revalidates rather than re-downloading — a 304 costs nothing and
          // is the honest answer when nothing has changed.
          cache: "no-cache",
        });
        if (!alive.current) return;
        const at = new Date();
        fetchedAtRef.current = at;
        setState({ data, error: null, fetchedAt: at, failingSince: null });
      } catch (err) {
        if (!alive.current || controller.signal.aborted) return;
        if (!retrying) {
          // A phone coming back from the background drops its first fetch
          // routinely; one retry before telling the badge keeps that honest
          // without crying wolf.
          retry = window.setTimeout(() => {
            retry = null;
            void load(true);
          }, RETRY_MS);
          return;
        }
        setState((prev) => ({
          ...prev,
          error: err as Error,
          failingSince: prev.failingSince ?? new Date(),
        }));
      } finally {
        inFlight = false;
      }
    };

    // A tab coming back — visible again, focused, or back online — refetches
    // now instead of waiting out a timer the browser may have suspended.
    const wake = () => {
      if (document.visibilityState === "hidden") return;
      // A retry already scheduled is a fetch already on its way; a wake in
      // that window must not start a second one beside it.
      if (retry !== null) return;
      if (shouldRefetchOnWake(fetchedAtRef.current, Date.now(), refreshMs)) void load();
    };

    void load();
    const id = window.setInterval(() => void load(), refreshMs);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    return () => {
      alive.current = false;
      controller.abort();
      window.clearInterval(id);
      if (retry !== null) window.clearTimeout(retry);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", wake);
    };
  }, [refreshMs]);

  return state;
}

/**
 * `tiles/manifest.json`, already rebased onto `DATA_BASE_URL`.
 *
 * `undefined` while loading, `null` when the read failed — the map needs to
 * tell "not yet" from "not there", and a single nullable cannot.
 */
export function useManifest(): TilesManifest | null | undefined {
  const [manifest, setManifest] = useState<TilesManifest | null | undefined>(
    undefined,
  );

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    fetchJson<TilesManifest>("tiles/manifest.json", {
      signal: controller.signal,
    })
      .then((m) => {
        if (alive) setManifest(rebaseManifest(m));
      })
      .catch((err) => {
        if (!alive || controller.signal.aborted) return;
        console.error("tiles/manifest.json could not be read", err);
        setManifest(null);
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, []);

  return manifest;
}

// --- the live collections --------------------------------------------------

/** The five GeoJSON files, and what they are called in `LiveData`. */
const LIVE_FILES = {
  alerts: "latest/alerts.geojson",
  fires: "latest/fires.geojson",
  detections: "latest/detections.geojson",
  quakes: "latest/quakes.geojson",
  drought: "latest/drought.geojson",
} as const;

type LiveKey = keyof typeof LIVE_FILES;

const EMPTY_LIVE: Omit<LiveData, "conditions"> = {
  alerts: null,
  fires: null,
  detections: null,
  quakes: null,
  drought: null,
};

/**
 * Every live feature collection, re-read on the same interval as conditions.
 *
 * One `Promise.allSettled`, not five awaits: a 404 on `detections.geojson`
 * (FIRMS is a tier-C source and does go away) must not stop the alerts from
 * arriving. A collection that fails keeps its last good value rather than
 * blanking — the map going empty is a much worse lie than the map being a
 * cycle behind, and the staleness badge is the thing that says which.
 */
export function useLiveCollections(
  refreshMs: number = REFRESH_MS,
): Omit<LiveData, "conditions"> {
  const [state, setState] = useState<Omit<LiveData, "conditions">>(EMPTY_LIVE);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    const load = async () => {
      const keys = Object.keys(LIVE_FILES) as LiveKey[];
      const results = await Promise.allSettled(
        keys.map((k) =>
          fetchJson<LiveCollection<never>>(LIVE_FILES[k], {
            signal: controller.signal,
          }),
        ),
      );
      if (!alive) return false;
      // Logged outside the updater: React 18's StrictMode calls a state
      // updater twice in development, and a warning is not idempotent.
      results.forEach((result, i) => {
        if (result.status === "rejected" && !controller.signal.aborted) {
          console.warn(`${LIVE_FILES[keys[i]]} could not be read`, result.reason);
        }
      });
      setState((prev) => {
        const next = { ...prev };
        results.forEach((result, i) => {
          if (result.status === "fulfilled") {
            // The cast is the one place the five differently-typed property
            // bags meet; each accessor narrows again at its own layer.
            (next as Record<string, unknown>)[keys[i]] = result.value;
          }
        });
        return next;
      });
      return results.some((r) => r.status === "fulfilled");
    };

    // The same wake-up rule as the conditions poll: a tab coming back
    // refetches now, so the alert and fire layers follow the badge.
    let lastAt: Date | null = null;
    let inFlight = false;
    const loadAndStamp = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const ok = await load();
        // Stamp on success only, as the conditions poll does: a wake during
        // an outage must still retry.
        if (ok) lastAt = new Date();
      } finally {
        inFlight = false;
      }
    };
    const wake = () => {
      if (document.visibilityState === "hidden") return;
      if (shouldRefetchOnWake(lastAt, Date.now(), refreshMs)) void loadAndStamp();
    };

    void loadAndStamp();
    const id = window.setInterval(() => void loadAndStamp(), refreshMs);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    return () => {
      alive = false;
      controller.abort();
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", wake);
    };
  }, [refreshMs]);

  return state;
}

// --- one place -------------------------------------------------------------

export interface PlacePageState {
  data: PlacePage | null;
  /** True while a fetch for the *current* id is outstanding. */
  loading: boolean;
  error: Error | null;
}

/**
 * `latest/{ns}/{slug}.json` for one place id, or nothing when `id` is null.
 *
 * Fetched on demand rather than up front: there are ~2,000 of these files and
 * the panel needs exactly one at a time. They are also the cacheable half of
 * the tree now that they carry no clock (see `readingStaleness`), so a second
 * visit to the same station is a 304.
 *
 * `generatedAt` is the publisher's clock — pass `conditions.generated_at` and
 * the page is re-read whenever the tree does move on. See the effect below.
 */
export function usePlacePage(id: string | null, generatedAt?: string | null): PlacePageState {
  const [state, setState] = useState<PlacePageState>({
    data: null,
    loading: false,
    error: null,
  });

  /** The id whose data is on screen, so a refresh can be told from a move. */
  const shown = useRef<string | null>(null);

  useEffect(() => {
    if (!id) {
      shown.current = null;
      setState({ data: null, loading: false, error: null });
      return;
    }
    let alive = true;
    const controller = new AbortController();
    // Only a change of ID blanks the panel. Showing one station's readings
    // under another station's name for the length of a round trip is a lie;
    // showing THIS station's readings from one cycle ago while the refresh is
    // in flight is not, and blanking on every publish would make an open panel
    // flicker every sixty seconds.
    const moved = shown.current !== id;
    shown.current = id;
    setState((prev) =>
      moved ? { data: null, loading: true, error: null } : { ...prev, loading: true, error: null },
    );

    fetchJson<PlacePage>(`latest/${id}.json`, { signal: controller.signal })
      .then((data) => {
        if (alive) setState({ data, loading: false, error: null });
      })
      .catch((err: Error) => {
        if (!alive || controller.signal.aborted) return;
        setState({ data: null, loading: false, error: err });
      });

    return () => {
      alive = false;
      controller.abort();
    };
    // `generatedAt` is the publisher's clock, from `conditions.json`, which
    // this app re-reads every 60 seconds. Without it in the deps the page is
    // fetched ONCE per id and an open panel goes on showing the readings it
    // opened with, however long the tab stays up — while the dot behind it
    // updates. Panel and map then disagree about the same gauge, which is the
    // one thing this app must never do. With it, the refetch happens exactly
    // when there is something new to read and not one request more.
  }, [id, generatedAt]);

  return state;
}

/**
 * `normals/{ns}/{slug}.json` for one place: its baselines, or `null` when the
 * tree says it has none.
 *
 * Most places have no baseline at all (the file exists only for places that
 * do), so a **404 is the ordinary answer** and resolves to `null`. Any other
 * failure stays `undefined`: a 500 or a dropped connection is not evidence
 * that a site has no record, and a reader must never be told it is. Re-read
 * on `generatedAt` like the place page.
 */
export function useNormals(
  id: string | null,
  generatedAt?: string | null,
): Normals | null | undefined {
  const [state, setState] = useState<{ id: string | null; data: Normals | null | undefined }>({
    id: null,
    data: undefined,
  });

  useEffect(() => {
    if (!id) {
      setState({ id: null, data: null });
      return;
    }
    let alive = true;
    const controller = new AbortController();
    setState((prev) => (prev.id === id ? prev : { id, data: undefined }));
    fetchJson<Normals>(`normals/${id}.json`, { signal: controller.signal })
      .then((data) => {
        if (alive) setState({ id, data });
      })
      .catch((err: unknown) => {
        if (!alive || controller.signal.aborted) return;
        const absent = err instanceof HttpError && err.status === 404;
        setState({ id, data: absent ? null : undefined });
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [id, generatedAt]);

  return id && state.id === id ? state.data : id ? undefined : null;
}

export interface SnowPollState {
  data: SnowState | null;
  error: Error | null;
}

/**
 * `latest/snow.json` on the conditions cadence, with the same wake-up rule.
 * A missing file (a tree published before the snow artifact existed) is a
 * `null`, not an error: the snow layer is simply absent.
 */
export function useSnow(refreshMs: number = REFRESH_MS): SnowPollState {
  const [state, setState] = useState<SnowPollState>({ data: null, error: null });
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    let lastAt: Date | null = null;
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await fetchJson<SnowState>("latest/snow.json", {
          signal: controller.signal,
          cache: "no-cache",
        });
        if (!alive) return;
        lastAt = new Date();
        setState({ data, error: null });
      } catch (err) {
        if (!alive || controller.signal.aborted) return;
        const e = err as Error & { status?: number };
        if (e.status === 404 || /404/.test(e.message)) {
          lastAt = new Date();
          setState({ data: null, error: null });
        } else {
          setState((prev) => ({ ...prev, error: e }));
        }
      } finally {
        inFlight = false;
      }
    };
    const wake = () => {
      if (document.visibilityState === "hidden") return;
      if (shouldRefetchOnWake(lastAt, Date.now(), refreshMs)) void load();
    };
    void load();
    const id = window.setInterval(() => void load(), refreshMs);
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
    return () => {
      alive = false;
      controller.abort();
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("focus", wake);
      window.removeEventListener("online", wake);
    };
  }, [refreshMs]);
  return state;
}


// --- the identity registry ----------------------------------------------------

/** One record per id for the life of the page: a place's identity does not change while it is open. */
const ID_RECORDS = new Map<string, Promise<IdRecord | null>>();

/**
 * `id/<id>.json` for the focused place: where else it lives (the commons, the
 * agencies). A 404 is a place without an identity entry (a superseded gauge)
 * and is `null`, not an error. Fetched once per id and cached for the page.
 */
export function useIdRecord(id: string | null): { record: IdRecord | null; error: boolean } {
  const [state, setState] = useState<{ id: string | null; record: IdRecord | null; error: boolean }>({
    id: null,
    record: null,
    error: false,
  });
  useEffect(() => {
    if (!id) return;
    let live = true;
    let p = ID_RECORDS.get(id);
    if (!p) {
      // A hung fetch must not park this id's promise for the life of the page.
      p = fetch(`${ID_BASE_URL}/${id}.json`, { signal: AbortSignal.timeout(15_000) })
        .then(async (res) => {
          if (res.status === 404) return null;
          if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
          return (await res.json()) as IdRecord;
        });
      ID_RECORDS.set(id, p);
    }
    p.then(
      (record) => live && setState({ id, record, error: false }),
      () => {
        ID_RECORDS.delete(id);
        if (live) setState({ id, record: null, error: true });
      },
    );
    return () => {
      live = false;
    };
  }, [id]);
  return state.id === id ? { record: state.record, error: state.error } : { record: null, error: false };
}

/** Test seam. */
export function __resetIdRecordsForTest(): void {
  ID_RECORDS.clear();
}


// --- the stream network ---------------------------------------------------------

let REACHES: Promise<ReachFeature[] | null> | null = null;

/**
 * `network/reaches.geojson`, once per page: a few thousand lines that never
 * change between publishes. A 404 (the ingest has not run) is "no network",
 * not an error — the twin draws without it.
 */
export function useReaches(): ReachFeature[] | null {
  const [reaches, setReaches] = useState<ReachFeature[] | null>(null);
  useEffect(() => {
    let live = true;
    REACHES ??= fetchJson<{ features: ReachFeature[] }>("network/reaches.geojson")
      .then((fc) => fc.features)
      .catch(() => null);
    REACHES.then((r) => live && setReaches(r));
    return () => {
      live = false;
    };
  }, []);
  return reaches;
}

/** Test seam. */
export function __resetReachesForTest(): void {
  REACHES = null;
}

let SPECIES: Promise<SpeciesIndex | null> | null = null;

/**
 * `latest/species.json` — what has been recorded living in each watershed.
 *
 * Fetched once per page and shared, like the reach network: it is a few
 * hundred kilobytes that changes quarterly, and every area's list is read
 * from the one file rather than from a request per watershed. A tree
 * published before the species ingest ran simply has none, and the layer
 * draws nothing rather than an error.
 */
export function useSpecies(): SpeciesIndex | null {
  const [index, setIndex] = useState<SpeciesIndex | null>(null);
  useEffect(() => {
    let live = true;
    SPECIES ??= fetchJson<SpeciesIndex>("latest/species.json").catch(() => null);
    SPECIES.then((s) => live && setIndex(s));
    return () => {
      live = false;
    };
  }, []);
  return index;
}

/** Test seam. */
export function __resetSpeciesForTest(): void {
  SPECIES = null;
}

/**
 * `species/{slug}.json` for one taxon, or `null` when the tree has none.
 *
 * A 404 is the ordinary answer for a taxon that fell below the record floor,
 * and it resolves to `null`; any other failure stays `undefined`, so the
 * modal never reports a broken fetch as "nothing recorded" (`useNormals`
 * makes the same distinction, for the same reason).
 */
export function useSpeciesRecord(id: string | null): SpeciesRecord | null | undefined {
  const [state, setState] = useState<{ id: string | null; data: SpeciesRecord | null | undefined }>({
    id: null,
    data: undefined,
  });
  useEffect(() => {
    if (!id) {
      setState({ id: null, data: null });
      return;
    }
    let alive = true;
    const controller = new AbortController();
    setState((prev) => (prev.id === id ? prev : { id, data: undefined }));
    fetchJson<SpeciesRecord>(`${id}.json`, { signal: controller.signal }).catch(async err => {
      if (!(err instanceof HttpError) || err.status !== 404 || !/^species\/gbif-\d+$/.test(id)) throw err;
      const shard = Number(id.split('gbif-')[1]) % 64;
      const catalog = await fetchJson<{species:Record<string,SpeciesRecord>}>(`species/catalog/${shard}.json`, {signal:controller.signal});
      const record = catalog.species[id];
      if (!record) throw err;
      return record;
    })
      .then((data) => alive && setState({ id, data }))
      .catch((err: unknown) => {
        if (!alive || controller.signal.aborted) return;
        const absent = err instanceof HttpError && err.status === 404;
        setState({ id, data: absent ? null : undefined });
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [id]);
  return id && state.id === id ? state.data : id ? undefined : null;
}

/** `latest/flow_network.json` on the conditions cadence; null until it exists. */
export function useFlowNetwork(refreshMs: number = REFRESH_MS): FlowNetwork | null {
  const [data, setData] = useState<FlowNetwork | null>(null);
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const next = await fetchJson<FlowNetwork>("latest/flow_network.json", { signal: controller.signal, cache: "no-cache" });
        if (alive) setData(next);
      } catch {
        // 404 or a blip: keep what we had; a missing network is drawn as "no pace".
      } finally {
        inFlight = false;
      }
    };
    void load();
    const id = window.setInterval(() => void load(), refreshMs);
    const wake = () => {
      if (document.visibilityState !== "hidden") void load();
    };
    document.addEventListener("visibilitychange", wake);
    return () => {
      alive = false;
      controller.abort();
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [refreshMs]);
  return data;
}
